import { Box, Chip, Tooltip } from "@mui/material"

export type TagWithFrequency = {
  id: string
  name: string
  count: number
}

type TagCloudProps = {
  tags: TagWithFrequency[]
  activeTagIds: string[]
  onToggle: (tagId: string) => void
  disabledTagIds?: string[]
  emptyLabel?: string
}

const MIN_FONT_SIZE_REM = 0.9
const MAX_FONT_SIZE_REM = 1.6

const computeFontSize = (count: number, min: number, max: number) => {
  if (max <= min) {
    return `${MIN_FONT_SIZE_REM}rem`
  }
  const ratio = (count - min) / (max - min)
  const fontSize = MIN_FONT_SIZE_REM + ratio * (MAX_FONT_SIZE_REM - MIN_FONT_SIZE_REM)
  return `${fontSize.toFixed(2)}rem`
}

export const TagCloud = ({
  tags,
  activeTagIds,
  onToggle,
  disabledTagIds = [],
  emptyLabel = "Keine Tags vorhanden",
}: TagCloudProps) => {
  if (!tags.length) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          fontStyle: "italic",
          minHeight: 48,
        }}
      >
        {emptyLabel}
      </Box>
    )
  }

  const max = Math.max(...tags.map((t) => t.count))
  const min = Math.min(...tags.map((t) => t.count))
  const disabledSet = new Set(disabledTagIds)
  const activeSet = new Set(activeTagIds)

  const sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name, "de"))

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      {sortedTags.map((tag) => {
        const isActive = activeSet.has(tag.id)
        const isDisabled = disabledSet.has(tag.id)
        const fontSize = computeFontSize(tag.count, min, max)

        const chip = (
          <Chip
            label={tag.name}
            onClick={() => (!isDisabled ? onToggle(tag.id) : undefined)}
            disabled={isDisabled}
            sx={{
              borderRadius: 9999,
              px: 1.5,
              py: 0.5,
              fontWeight: isActive ? 600 : 400,
              cursor: isDisabled ? "not-allowed" : "pointer",
              transition: "transform 0.12s ease, background-color 0.12s ease",
              transform: isActive ? "scale(1.05)" : "none",
              bgcolor: isActive ? "success.main" : "background.paper",
              color: isActive ? "success.contrastText" : "text.primary",
              border: isActive ? "none" : "1px solid",
              borderColor: isActive ? "transparent" : "divider",
              "&:hover": !isDisabled
                ? isActive
                  ? {
                      bgcolor: "error.main",
                      color: "error.contrastText",
                    }
                  : {
                      bgcolor: "primary.light",
                      color: "primary.contrastText",
                    }
                : {},
              "& .MuiChip-label": {
                fontSize,
                px: 0.5,
              },
            }}
          />
        )

        return isDisabled ? (
          <Tooltip key={tag.id} title="In aktueller Kombination nicht verfügbar">
            <span>{chip}</span>
          </Tooltip>
        ) : (
          <Tooltip key={tag.id} title={`Verknüpft mit ${tag.count} Prüfverfahren`}>
            {chip}
          </Tooltip>
        )
      })}
    </Box>
  )
}

export default TagCloud
